import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'sidebarPinnedItems';
const MAX_PINS = 6;

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((k) => typeof k === 'string') : [];
  } catch {
    return [];
  }
};

/**
 * Lets a user pin nav items to the top of the sidebar. Persisted per-browser
 * in localStorage; keys are the same `item.key` values used in navigationConfig.
 */
export const usePinnedNavItems = () => {
  const [pinnedKeys, setPinnedKeys] = useState(readStored);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setPinnedKeys(readStored());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const persist = useCallback((next) => {
    setPinnedKeys(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // localStorage unavailable (private mode, quota) — pin state just won't persist.
    }
  }, []);

  const isPinned = useCallback((key) => pinnedKeys.includes(key), [pinnedKeys]);

  const togglePin = useCallback((key) => {
    persist(
      pinnedKeys.includes(key)
        ? pinnedKeys.filter((k) => k !== key)
        : [...pinnedKeys, key].slice(-MAX_PINS),
    );
  }, [pinnedKeys, persist]);

  return { pinnedKeys, isPinned, togglePin };
};
